import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageBody, Spinner, backLinkVariants } from '@wundo/ui';
import type { TrackedPageRoute } from '@wundo/shared/pageVisit.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { CohortFilter } from '@/components/pickers/CohortFilter.tsx';
import { PageUsageTable } from '@/components/dashboard/PageUsageTable.tsx';
import { PageUsageUsersDialog } from '@/components/dashboard/PageUsageUsersDialog.tsx';
import { OnboardingStepsCard } from '@/components/dashboard/OnboardingStepsCard.tsx';
import { PAGE_USAGE_TRACKED_SINCE } from '@/components/dashboard/trackedPages.ts';
import { fetchPageUsage, type DashboardScope } from '@/lib/dashboard.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { useCohortInOrganisation } from '@/lib/useCohortInOrganisation.ts';

export interface PageUsageSearch {
  /** Which cohort the usage is about. Absent is the whole organisation, or every one. */
  cohort: string | undefined;
}

export const Route = createFileRoute('/_app/dashboard/page-usage')({
  staticData: { orgScope: 'optional' },
  validateSearch: (search: Record<string, unknown>): PageUsageSearch => ({
    cohort:
      typeof search.cohort === 'string' && search.cohort !== ''
        ? search.cohort
        : undefined,
  }),
  component: PageUsageRoute,
});

function PageUsageRoute() {
  const { cohort } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <PageUsagePage
      cohort={cohort}
      onSelectCohort={(next) =>
        void navigate({ to: '/dashboard/page-usage', search: { cohort: next } })
      }
    />
  );
}

export interface PageUsagePageProps {
  cohort: string | undefined;
  onSelectCohort: (cohort: string | undefined) => void;
}

export function PageUsagePage({
  cohort: pickedCohort,
  onSelectCohort,
}: PageUsagePageProps) {
  const organisationId = useOrganisationFilter()?.id;
  /* A cohort belongs to one organisation, so across all of them there is none to pick. */
  const { cohort, settled } = useCohortInOrganisation(
    organisationId ? pickedCohort : undefined,
    organisationId,
  );
  const [selected, setSelected] = useState<TrackedPageRoute | null>(null);

  const scope: DashboardScope = { cohortId: cohort, organisationId };

  const usage = useQuery({
    queryKey: ['pageUsage', cohort ?? null, organisationId ?? null],
    queryFn: () => fetchPageUsage(scope),
    enabled: settled,
    throwOnError: true,
  });

  return (
    <>
      <Link
        to='/dashboard'
        search={{ cohort }}
        className={backLinkVariants({ className: 'mb-2.5' })}
      >
        Dashboard
      </Link>

      <PageHeader
        title='Page usage'
        subtitle={`Which pages of the web app people open. Tracking since ${PAGE_USAGE_TRACKED_SINCE}.`}
        filters={
          organisationId && (
            <CohortFilter
              value={cohort}
              onChange={onSelectCohort}
              organisationId={organisationId}
            />
          )
        }
      />

      <PageBody>
        {usage.data === undefined ? (
          <p className='flex items-center gap-2 text-sm text-ink-muted'>
            <Spinner />
            Loading page usage…
          </p>
        ) : (
          <div className='flex flex-col gap-6'>
            <PageUsageTable
              pages={usage.data.data.pages}
              totalUsers={usage.data.data.total_users}
              onSelectPage={setSelected}
            />
            <OnboardingStepsCard pages={usage.data.data.pages} />
          </div>
        )}
      </PageBody>

      <PageUsageUsersDialog
        route={selected}
        scope={scope}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
